import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Wand2, Search, AlertCircle } from "lucide-react";
import { suggestDomainNames, type DomainSuggestion } from "@/lib/domains.functions";
import { toast } from "sonner";

const TLD_OPTIONS = [".com", ".io", ".ai", ".app", ".dev", ".co", ".xyz", ".tech"];

export function AiDomainResearch({
  onPick,
}: {
  /** Called when the user clicks "Check availability" on a suggestion. */
  onPick: (query: string) => void;
}) {
  const [brief, setBrief] = useState("");
  const [tlds, setTlds] = useState<string[]>([".com", ".io", ".ai"]);
  const [count, setCount] = useState(8);
  const fn = useServerFn(suggestDomainNames);

  const mut = useMutation({
    mutationFn: async () => fn({ data: { brief: brief.trim(), count, tlds } }),
    onError: (e: Error) => toast.error(e.message || "AI request failed"),
  });

  const toggleTld = (t: string) =>
    setTlds((curr) => (curr.includes(t) ? curr.filter((x) => x !== t) : [...curr, t]));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (brief.trim().length < 3) return;
    if (tlds.length === 0) {
      toast.error("Pick at least one TLD");
      return;
    }
    mut.mutate();
  };

  return (
    <section className="py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-6 sm:p-10 shadow-card">
          <div className="absolute inset-0 -z-10 bg-hero-glow opacity-50" />

          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI domain research
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
              Tell us about your <span className="text-gradient">brand</span>
            </h2>
            <p className="mt-3 text-muted-foreground">
              Describe what you're building and we'll brainstorm short, brandable names paired with the right TLD.
            </p>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-4 max-w-2xl mx-auto">
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value.slice(0, 500))}
              placeholder="e.g. an AI-powered scheduling app for small dental clinics in the GCC"
              rows={3}
              className="w-full resize-none rounded-md border border-border bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
            />

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">TLDs</span>
              {TLD_OPTIONS.map((t) => {
                const active = tlds.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTld(t)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      active
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background hover:bg-secondary"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Suggestions:
                <select
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value))}
                  className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                >
                  {[5, 8, 10, 15, 20].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <button
                type="submit"
                disabled={mut.isPending || brief.trim().length < 3}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-white shadow-glow disabled:opacity-60"
              >
                {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {mut.isPending ? "Thinking..." : "Generate names"}
              </button>
            </div>
          </form>

          <AnimatePresence mode="wait">
            {mut.isPending && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="mt-8 grid gap-2 sm:grid-cols-2 max-w-3xl mx-auto"
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-20 rounded-lg border border-border bg-background/40 relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                ))}
              </motion.div>
            )}

            {mut.data && !mut.isPending && (
              <motion.div
                key="out"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-8 max-w-3xl mx-auto"
              >
                {mut.data.suggestions.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground">
                    No suggestions came back — try a longer, more specific brief.
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {mut.data.suggestions.map((s, i) => (
                      <SuggestionCard key={i} s={s} onPick={() => onPick(s.name + s.tld)} />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {mut.isError && (
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" /> {mut.error?.message || "Something went wrong."}
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function SuggestionCard({ s, onPick }: { s: DomainSuggestion; onPick: () => void }) {
  return (
    <div className="group rounded-lg border border-border bg-background p-4 hover:border-primary/60 transition">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-display text-lg font-semibold truncate">
          {s.name}<span className="text-primary">{s.tld}</span>
        </div>
        <button
          onClick={onPick}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-gradient-brand px-2.5 py-1.5 text-xs font-semibold text-white shadow-glow opacity-90 group-hover:opacity-100"
        >
          <Search className="h-3 w-3" /> Check
        </button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{s.rationale}</p>
    </div>
  );
}
